import { Ref, useEffect } from 'react';
import { useTranslate } from 'core/hooks/useTranslate';

type UseInputTextParams = {
  disabled?: boolean;
  icon?: string;
  iconAriaLabel?: string;
  onClickIcon?: (...args: unknown[]) => void;
  ref?: Ref<HTMLInputElement>;
  referer?: Ref<HTMLInputElement>;
};

function useInputText({ disabled, icon, iconAriaLabel, onClickIcon, ref, referer }: UseInputTextParams) {
  const { t } = useTranslate();

  const refInput = referer ?? ref;

  const getIconAriaLabel = (iconType: string): string => {
    const iconLabels: Record<string, string> = {
      visible: t('buttons:Show password'),
      'visible-off': t('buttons:Hide password'),
      search: t('buttons:Search'),
      clear: t('buttons:Clear field'),
      calendar: t('buttons:Open calendar'),
      eye: t('buttons:Show password'),
      'eye-off': t('buttons:Hide password'),
    };

    return iconLabels[iconType] || t('buttons:Click {{icon}} icon', { icon: iconType });
  };

  useEffect(() => {
    let isMouseDown = false;
    let keyboardNavTimeout: ReturnType<typeof setTimeout> | null = null;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Tab') {
        document.body.classList.add('keyboard-nav');
        if (keyboardNavTimeout) {
          clearTimeout(keyboardNavTimeout);
        }
        keyboardNavTimeout = setTimeout(() => {
          document.body.classList.remove('keyboard-nav');
        }, 150);
      }
    }

    function handleMouseDown() {
      isMouseDown = true;
      document.body.classList.remove('keyboard-nav');
      if (keyboardNavTimeout) {
        clearTimeout(keyboardNavTimeout);
        keyboardNavTimeout = null;
      }
      setTimeout(() => {
        isMouseDown = false;
      }, 0);
    }

    function handleFocusIn(e: FocusEvent) {
      const target = e.target as HTMLElement;
      if (target.classList.contains('input-text__input')) {
        if (document.body.classList.contains('keyboard-nav') && !isMouseDown) {
          target.classList.add('focused-by-keyboard');
          target.classList.remove('focused-by-mouse');
        } else {
          target.classList.add('focused-by-mouse');
          target.classList.remove('focused-by-keyboard');
        }
      }
    }

    function handleFocusOut(e: FocusEvent) {
      const target = e.target as HTMLElement;
      if (target.classList.contains('input-text__input')) {
        target.classList.remove('focused-by-keyboard', 'focused-by-mouse');
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);

    return () => {
      if (keyboardNavTimeout) {
        clearTimeout(keyboardNavTimeout);
      }
      document.body.classList.remove('keyboard-nav');
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
    };
  }, []);

  const getIconAriaLabelValue = icon ? (iconAriaLabel || getIconAriaLabel(icon)) : undefined;

  const isIconClickable = typeof onClickIcon === 'function' && !disabled;

  return {
    refInput,
    getIconAriaLabelValue,
    isIconClickable,
  };
}

export default useInputText;
